import { supabase } from "~/integrations/supabase/client";

export interface Transportista {
  id: string;
  name: string;
  lastname: string;
  email: string;
}

// Cache for transportistas to avoid repeated API calls
let transportistasCache: Transportista[] | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const fetchTransportistas = async (
  useCache = true
): Promise<Transportista[]> => {
  try {
    // Check if we have cached data and it's still valid
    if (
      useCache &&
      transportistasCache &&
      Date.now() - cacheTimestamp < CACHE_DURATION
    ) {
      return transportistasCache;
    }

    const { data, error } = await supabase
      .from("profiles")
      .select("id, name, lastname, email")
      .eq("role", "transportista")
      .order("name", { ascending: true }); // Add ordering for consistency

    if (error) {
      console.error("Error fetching transportistas:", error);
      throw error;
    }

    // Update cache
    transportistasCache = data || [];
    cacheTimestamp = Date.now();

    return transportistasCache;
  } catch (error) {
    console.error("Failed to fetch transportistas:", error);
    // Return cached data if available, even if stale
    return transportistasCache || [];
  }
};

// Function to clear cache when needed
export const clearTransportistasCache = () => {
  transportistasCache = null;
  cacheTimestamp = 0;
};

/**
 * Assigns a transportista to a sales request
 */
export const assignTransportista = async (
  salesRequestId: string,
  transportistaId: string
): Promise<void> => {
  try {
    console.log(
      `🚛 Assigning transportista ${transportistaId} to sales request ${salesRequestId}`
    );

    // Get current user for history entry
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const { error } = await supabase
      .from("sales_request")
      .update({
        assigned_transportista: transportistaId,
        updated_at: new Date().toISOString(),
      })
      .eq("id", salesRequestId);

    if (error) {
      console.error("❌ Error assigning transportista:", error);
      throw error;
    }

    // Record history entry for transition from "facturado" to "in_transit"
    await supabase.from("sales_request_status_history").insert({
      sales_request_id: salesRequestId,
      old_status: "facturado",
      new_status: "in_transit",
      changed_by: user?.id || null,
      changed_at: new Date().toISOString(),
      comments: "Transportista asignado",
    });

    console.log(
      `✅ Successfully assigned transportista ${transportistaId} to sales request ${salesRequestId}`
    );
  } catch (error) {
    console.error("❌ Failed to assign transportista:", error);
    throw error;
  }
};
